import { create } from 'zustand';
import type {
  BeatState,
  LiveOverrides,
  LyricDisplayMode,
  PreparedTrack,
  QueueTrackPreview,
  VisualEvent,
  VisualStyle,
} from '../types';
import { DEFAULT_BPM } from '../types';

interface PartyState {
  running: boolean;
  inputMode: 'spotify' | 'demo' | 'mic' | 'spotify-mic';
  spotifyConnected: boolean;
  spotifyUsername: string;
  clientId: string;
  overlayVisible: boolean;
  micEnergy: number;
  micOnset: number;

  currentTrack: PreparedTrack | null;
  nextTrack: QueueTrackPreview | null;
  queueTracks: QueueTrackPreview[];
  preparedTrackIds: string[];
  spotifyDeviceName: string;
  spotifyDeviceType: string;
  syncStatus: 'idle' | 'polling' | 'synced' | 'stale' | 'error';
  lastSyncAt: number;
  progressMs: number;
  isPlaying: boolean;
  beat: BeatState;
  activeEvent: VisualEvent | null;
  displayLyric: string;
  lyricsStatus: 'loading' | 'synced' | 'missing' | 'disabled';

  visualStyle: VisualStyle;
  overrides: LiveOverrides;
  manualBpm: number | null;
  tapTimes: number[];
  sceneOverride: VisualEvent | null;

  setRunning: (running: boolean) => void;
  setInputMode: (inputMode: PartyState['inputMode']) => void;
  setSpotifyConnected: (connected: boolean, username?: string) => void;
  setClientId: (id: string) => void;
  setMicSignal: (energy: number, onset: number) => void;
  toggleOverlay: () => void;
  setCurrentTrack: (track: PreparedTrack | null) => void;
  setNextTrack: (track: QueueTrackPreview | null) => void;
  setQueueTracks: (tracks: QueueTrackPreview[]) => void;
  markTrackPrepared: (trackId: string) => void;
  setSpotifyDevice: (name: string, type: string) => void;
  setSyncStatus: (status: PartyState['syncStatus']) => void;
  setPlayback: (progressMs: number, isPlaying: boolean, beat: BeatState) => void;
  setActiveEvent: (event: VisualEvent | null) => void;
  setDisplayLyric: (text: string) => void;
  setLyricsStatus: (status: PartyState['lyricsStatus']) => void;
  setVisualStyle: (style: VisualStyle) => void;
  setManualBpm: (bpm: number | null) => void;
  registerTap: () => void;
  adjustIntensity: (delta: number) => void;
  toggleLyrics: () => void;
  setLyricsEnabled: (enabled: boolean) => void;
  setLyricDisplayMode: (mode: LyricDisplayMode) => void;
  cycleLyricDisplayMode: () => void;
  toggleForceLyricMode: () => void;
  toggleBlackout: () => void;
  adjustStrobeBoost: (delta: number) => void;
  skipScene: () => void;
  toggleSceneLock: () => void;
  setSceneLock: (locked: boolean) => void;
  setForcedScene: (sceneId: LiveOverrides['forcedSceneId']) => void;
}

const defaultBeat: BeatState = {
  bpm: DEFAULT_BPM,
  beatIndex: 0,
  beatPhase: 0,
  isBeat: false,
  beatStrength: 0,
  barIndex: 0,
};

const defaultOverrides: LiveOverrides = {
  intensityOffset: 0,
  lyricsEnabled: true,
  lyricDisplayMode: 'hooks',
  forceLyricMode: false,
  blackout: false,
  strobeBoost: 0,
  sceneLocked: false,
  forcedSceneId: null,
};

function sameVisualEvent(a: VisualEvent | null, b: VisualEvent | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.startMs === b.startMs
    && a.endMs === b.endMs
    && a.sceneId === b.sceneId
    && a.section === b.section
    && a.transition === b.transition
    && a.intensity === b.intensity
    && sameEventParams(a.params, b.params);
}

function sameEventParams(a: VisualEvent['params'], b: VisualEvent['params']) {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

export const usePartyStore = create<PartyState>((set, get) => ({
  running: false,
  inputMode: 'demo',
  spotifyConnected: false,
  spotifyUsername: '',
  clientId: '',
  overlayVisible: true,
  micEnergy: 0,
  micOnset: 0,

  currentTrack: null,
  nextTrack: null,
  queueTracks: [],
  preparedTrackIds: [],
  spotifyDeviceName: '',
  spotifyDeviceType: '',
  syncStatus: 'idle',
  lastSyncAt: 0,
  progressMs: 0,
  isPlaying: false,
  beat: defaultBeat,
  activeEvent: null,
  displayLyric: '',
  lyricsStatus: 'loading',

  visualStyle: 'festival',
  overrides: defaultOverrides,
  manualBpm: null,
  tapTimes: [],
  sceneOverride: null,

  setRunning: (running) => set({ running }),
  setInputMode: (inputMode) => set({ inputMode }),
  setSpotifyConnected: (connected, username = '') =>
    set({ spotifyConnected: connected, spotifyUsername: username }),
  setClientId: (clientId) => set({ clientId }),
  setMicSignal: (micEnergy, micOnset) => set((s) => (
    Math.abs(s.micEnergy - micEnergy) < 0.006 && Math.abs(s.micOnset - micOnset) < 0.006
      ? s
      : { micEnergy, micOnset }
  )),
  toggleOverlay: () => set((s) => ({ overlayVisible: !s.overlayVisible })),
  setCurrentTrack: (currentTrack) => set({ currentTrack }),
  setNextTrack: (nextTrack) => set({ nextTrack }),
  setQueueTracks: (queueTracks) => set({ queueTracks }),
  markTrackPrepared: (trackId) =>
    set((s) => ({
      preparedTrackIds: s.preparedTrackIds.includes(trackId)
        ? s.preparedTrackIds
        : [...s.preparedTrackIds, trackId].slice(-24),
      queueTracks: s.queueTracks.map((track) =>
        track.id === trackId ? { ...track, prepared: true } : track
      ),
    })),
  setSpotifyDevice: (spotifyDeviceName, spotifyDeviceType) =>
    set((s) => (
      s.spotifyDeviceName === spotifyDeviceName && s.spotifyDeviceType === spotifyDeviceType
        ? s
        : { spotifyDeviceName, spotifyDeviceType }
    )),
  setSyncStatus: (syncStatus) => set((s) => {
    const now = Date.now();
    if (s.syncStatus === syncStatus && now - s.lastSyncAt < 1000) return s;
    return { syncStatus, lastSyncAt: now };
  }),
  setPlayback: (progressMs, isPlaying, beat) => set({ progressMs, isPlaying, beat }),
  setActiveEvent: (activeEvent) => set((s) => (
    sameVisualEvent(s.activeEvent, activeEvent) ? s : { activeEvent }
  )),
  setDisplayLyric: (displayLyric) => set((s) => (
    s.displayLyric === displayLyric ? s : { displayLyric }
  )),
  setLyricsStatus: (lyricsStatus) => set((s) => (
    s.lyricsStatus === lyricsStatus ? s : { lyricsStatus }
  )),
  setVisualStyle: (visualStyle) => set({ visualStyle }),

  setManualBpm: (manualBpm) => set({ manualBpm }),

  registerTap: () => {
    const now = performance.now();
    const taps = [...get().tapTimes, now].slice(-8);
    set({ tapTimes: taps });
  },

  adjustIntensity: (delta) =>
    set((s) => ({
      overrides: {
        ...s.overrides,
        intensityOffset: Math.max(-0.5, Math.min(1, s.overrides.intensityOffset + delta)),
      },
    })),

  toggleLyrics: () =>
    set((s) => ({
      overrides: {
        ...s.overrides,
        lyricsEnabled: !s.overrides.lyricsEnabled,
        lyricDisplayMode: s.overrides.lyricsEnabled ? 'off' : 'hooks',
      },
    })),

  setLyricsEnabled: (lyricsEnabled) =>
    set((s) => ({
      overrides: {
        ...s.overrides,
        lyricsEnabled,
        lyricDisplayMode: lyricsEnabled
          ? s.overrides.lyricDisplayMode === 'off' ? 'hooks' : s.overrides.lyricDisplayMode
          : 'off',
      },
      displayLyric: lyricsEnabled ? s.displayLyric : '',
    })),

  setLyricDisplayMode: (lyricDisplayMode) =>
    set((s) => ({
      overrides: {
        ...s.overrides,
        lyricDisplayMode,
        lyricsEnabled: lyricDisplayMode !== 'off',
      },
      displayLyric: lyricDisplayMode === 'off' ? '' : s.displayLyric,
    })),

  cycleLyricDisplayMode: () =>
    set((s) => {
      const modes: LyricDisplayMode[] = ['hooks', 'line', 'full', 'off'];
      const currentIndex = modes.indexOf(s.overrides.lyricDisplayMode);
      const lyricDisplayMode = modes[(currentIndex + 1) % modes.length];
      return {
        overrides: {
          ...s.overrides,
          lyricDisplayMode,
          lyricsEnabled: lyricDisplayMode !== 'off',
        },
        displayLyric: lyricDisplayMode === 'off' ? '' : s.displayLyric,
      };
    }),

  toggleForceLyricMode: () =>
    set((s) => ({
      overrides: { ...s.overrides, forceLyricMode: !s.overrides.forceLyricMode },
    })),

  toggleBlackout: () =>
    set((s) => ({
      overrides: { ...s.overrides, blackout: !s.overrides.blackout },
    })),

  adjustStrobeBoost: (delta) =>
    set((s) => ({
      overrides: {
        ...s.overrides,
        strobeBoost: Math.max(0, Math.min(1, s.overrides.strobeBoost + delta)),
      },
    })),

  skipScene: () => {
    const track = get().currentTrack;
    if (!track) return;
    const progressMs = get().progressMs;
    const timeline = track.visualTimeline;
    const idx = timeline.findIndex((e) => progressMs >= e.startMs && progressMs < e.endMs);
    if (idx >= 0 && idx < timeline.length - 1) {
      set({ sceneOverride: timeline[idx + 1] });
    }
  },

  toggleSceneLock: () =>
    set((s) => ({
      overrides: { ...s.overrides, sceneLocked: !s.overrides.sceneLocked },
    })),

  setSceneLock: (sceneLocked) =>
    set((s) => ({
      overrides: { ...s.overrides, sceneLocked },
    })),

  setForcedScene: (forcedSceneId) =>
    set((s) => ({
      overrides: { ...s.overrides, forcedSceneId },
    })),
}));

export function getEffectiveIntensity(base: number): number {
  const { overrides } = usePartyStore.getState();
  return Math.max(0, Math.min(1.5, base + overrides.intensityOffset + overrides.strobeBoost * 0.35));
}
